import React, { useMemo } from "react";
import { Box, Typography, Tooltip, IconButton, useTheme } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { formatRate, getBookieLogoUrl } from "../../constants";
import MobileSGMCard from "../MobileSGMCard";
import ToggleableDate from "../ToggleableDate";

// Helper function to render odds consistently - moved from SGMPage
const renderOdds = (
  value,
  isPercentage = false,
  isNumber = false,
  threeDecimals = false,
  showInfoIcon = false
) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      height: "100%",
    }}
  >
    <Typography
      variant="subTitle1"
      sx={{
        padding: { xs: "4px", sm: "8px" },
        borderRadius: "4px",
        minWidth: "60px",
        fontWeight: 600,
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        lineHeight: "18px",
        backgroundColor: (theme) => theme.palette.eventBackground.main,
      }}
    >
      {isPercentage
        ? `${formatToThreeDigits(value)}%`
        : isNumber
        ? value
        : formatRate(value, threeDecimals, true)}
      {showInfoIcon && (
        <InfoOutlinedIcon
          sx={{
            fontSize: 16,
            opacity: 0.7,
            color: "primary.main",
          }}
        />
      )}
    </Typography>
  </Box>
);

// Helper function to format to three digits
const formatToThreeDigits = (number) => {
  const absNumber = Math.abs(number);
  let formattedNumber;

  if (absNumber >= 100) {
    formattedNumber = Math.round(absNumber).toString();
  } else if (absNumber >= 10) {
    formattedNumber = absNumber.toFixed(1);
  } else {
    formattedNumber = absNumber.toFixed(2);
  }

  return number < 0 ? "-" + formattedNumber : formattedNumber;
};

// Helper function to render EV values with appropriate styling
const renderEV = (value, theme) => {
  let color, backgroundColor;
  if (value >= 3) {
    color = "#35BD5B";
    backgroundColor = "rgba(53, 189, 91, 0.1)";
  } else if (value > 0 && value < 3) {
    color = "#FFA500";
    backgroundColor = "rgba(255, 165, 0, 0.1)";
  } else {
    color = "#FF0000";
    backgroundColor = "rgba(255, 0, 0, 0.1)";
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: { xs: "4px", sm: "8px" },
          borderRadius: "4px",
          border: `2px solid ${color}`,
          backgroundColor: backgroundColor,
          width: "100%",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {`${value.toFixed(2)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

const SGMColumns = ({
  columnWidths,
  handleColumnWidthChange,
  handleGroupedBetClick,
  copyToBetTracker,
  addToSGMBuilder,
  sgmSelections,
  isMobile,
}) => {
  const theme = useTheme();

  // Standard columns for regular view
  const columns = useMemo(
    () => [
      {
        field: "market",
        headerName: "Market",
        minWidth: 150,
        width: columnWidths["market"] || 150,
        filterable: false,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "key",
        headerName: "Selection",
        minWidth: 180,
        width: columnWidths["key"] || 180,
        filterable: false,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "bookie",
        headerName: "Bookie",
        minWidth: 140,
        width: columnWidths["bookie"] || 140,
        filterable: false,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box
            sx={{
              cursor: "pointer",
              width: "120px",
              height: "30px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={() => window.open(params.row.source)}
          >
            <img
              src={getBookieLogoUrl(params.row.bookie)}
              alt={params.row.bookie}
              style={{
                width: "120px",
                height: "30px",
                objectFit: "contain",
                borderRadius: "4px",
              }}
            />
          </Box>
        ),
      },
      {
        field: "bookieCount",
        headerName: "Count",
        renderHeader: () => (
          <Tooltip
            title="The number of alternative bookies used to calculate fair odds"
            placement="top"
          >
            <span>Count</span>
          </Tooltip>
        ),
        minWidth: 100,
        width: columnWidths["bookieCount"] || 100,
        headerAlign: "left",
        align: "left",
        type: "number",
        renderCell: (params) => (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              handleGroupedBetClick(params.row);
            }}
            sx={{
              cursor: "pointer",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            {renderOdds(params.value || 0, false, true, false, true)}
          </Box>
        ),
        filterable: false,
      },
      {
        field: "rate",
        headerName: "Odds",
        minWidth: 100,
        width: columnWidths["rate"] || 100,
        type: "number",
        headerAlign: "left",
        align: "left",
        filterable: false,
        valueFormatter: (params) => formatRate(params.value || 0),
        renderCell: (params) => renderOdds(params.value || 0),
      },
      {
        field: "bestAltOdds",
        headerName: "Best Alt",
        minWidth: 100,
        width: columnWidths["bestAltOdds"] || 100,
        type: "number",
        headerAlign: "left",
        align: "left",
        filterable: false,
        valueFormatter: (params) => formatRate(params.value || 0),
        renderCell: (params) => renderOdds(params.value || 0),
      },
      {
        field: "aveOdds",
        headerName: "Avg Odds",
        minWidth: 100,
        width: columnWidths["aveOdds"] || 100,
        type: "number",
        headerAlign: "left",
        align: "left",
        filterable: false,
        valueFormatter: (params) => formatRate(params.value || 0),
        renderCell: (params) => renderOdds(params.value || 0),
      },
      {
        field: "fairOdds",
        headerName: "Fair Odds",
        minWidth: 100,
        width: columnWidths["fairOdds"] || 100,
        type: "number",
        headerAlign: "left",
        align: "left",
        filterable: false,
        valueFormatter: (params) => formatRate(params.value || 0, true),
        renderCell: (params) => {
          // Display N/A if fair odds is 0 or not available
          if (!params.value || params.value === 0) {
            return "N/A";
          }
          return renderOdds(params.value || 0, false, false, true);
        },
      },
      {
        field: "ev",
        headerName: "EV",
        minWidth: 110,
        width: columnWidths["ev"] || 110,
        type: "number",
        headerAlign: "left",
        align: "left",
        filterable: false,
        valueFormatter: (params) =>
          params.value !== undefined ? `${params.value.toFixed(2)}%` : "N/A",
        renderCell: (params) => {
          // Display N/A if fair odds is 0 or not available, or if EV is undefined
          if (
            !params.row.fairOdds ||
            params.row.fairOdds === 0 ||
            params.value === undefined
          ) {
            return "N/A";
          }
          return renderEV(params.value, theme);
        },
      },
      {
        field: "oddsAveRatio",
        headerName: "Odds/Avg",
        minWidth: 110,
        width: columnWidths["oddsAveRatio"] || 110,
        type: "number",
        headerAlign: "left",
        align: "left",
        filterable: false,
        valueFormatter: (params) => {
          return params.value !== undefined
            ? `${((params.value - 1) * 100).toFixed(2)}%`
            : "N/A";
        },
        renderCell: (params) =>
          params.value !== undefined
            ? renderOdds((params.value - 1) * 100, true)
            : "N/A",
      },
      {
        field: "firstSecondRatio",
        headerName: "Odds/Alt",
        renderHeader: () => (
          <Tooltip
            title="The percentage difference between the Odds and the Best Alternative"
            placement="top"
          >
            <span>Odds/Alt</span>
          </Tooltip>
        ),
        minWidth: 110,
        width: columnWidths["firstSecondRatio"] || 110,
        type: "number",
        headerAlign: "left",
        align: "left",
        filterable: false,
        valueFormatter: (params) => {
          return params.value !== undefined
            ? `${((params.value - 1) * 100).toFixed(2)}%`
            : "N/A";
        },
        renderCell: (params) =>
          params.value !== undefined
            ? renderOdds((params.value - 1) * 100, true)
            : "N/A",
      },
      {
        field: "actions",
        headerName: "Copy",
        minWidth: 80,
        width: columnWidths["actions"] || 80,
        filterable: false,
        sortable: false,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Tooltip title="Copy to Bet Tracker">
            <IconButton
              onClick={() => copyToBetTracker(params.row)}
              size="small"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [columnWidths, handleGroupedBetClick, copyToBetTracker, theme]
  );

  // Mobile column for mobile view
  const mobileColumn = useMemo(
    () => ({
      field: "mobileCard",
      headerName: "SGM Opportunity",
      width: "100%",
      flex: 1,
      renderCell: (params) => (
        <MobileSGMCard
          row={params.row}
          addToSGMBuilder={addToSGMBuilder}
          sgmSelections={sgmSelections}
          handleGroupedBetClick={handleGroupedBetClick}
          copyToBetTracker={copyToBetTracker}
        />
      ),
      sortable: false,
      filterable: false,
    }),
    [addToSGMBuilder, sgmSelections, handleGroupedBetClick, copyToBetTracker]
  );

  // SGM action column
  const sgmActionColumn = useMemo(
    () => ({
      field: "sgmAction",
      headerName: "SGM",
      minWidth: 60,
      width: columnWidths["sgmAction"] || 60,
      filterable: false,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        // Check if this selection is already in the SGM Builder
        const selectionKey = `${params.row.market}:${params.row.key}:${params.row.bookie}`;
        const isSelected = sgmSelections.some(
          (selection) =>
            `${selection.market}:${selection.selection}:${selection.bookie}` ===
            selectionKey
        );

        return (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              addToSGMBuilder(params.row);
            }}
            size="small"
            color={isSelected ? "secondary" : "primary"}
          >
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        );
      },
    }),
    [columnWidths, addToSGMBuilder, sgmSelections]
  );

  // Combine columns based on mobile view
  const columnsWithSGMAction = useMemo(() => {
    if (isMobile) {
      return [mobileColumn]; // For mobile, only include the mobile card column
    }

    // For desktop view, add SGM action column at the beginning
    return [sgmActionColumn, ...columns];
  }, [columns, sgmActionColumn, mobileColumn, isMobile]);

  return {
    columns,
    mobileColumn,
    sgmActionColumn,
    columnsWithSGMAction,
  };
};

export default SGMColumns;
